import { toClinicDate, clinicToday } from '../../../utils/clinicTime';
import { PAYMENT_MODE_LABEL } from '../../../constants/payments';
import { DASH, human } from '../shared/profileKit';

// The Patient Profile's own labels. The column builder, badge and formatters are shared with the
// Medicine Profile in reports/shared/profileKit and re-exported here, so the report's files import
// everything from one place.

export * from '../shared/profileKit';

// ---------------------------------------------------------------- domain labels

export const CANCEL_OUTCOME_LABEL = {
  KEPT: 'Fee kept by hospital',
  REFUNDED: 'Fee refunded',
  HELD_AS_CREDIT: 'Fee held as credit',
  NOTHING_PAID: 'Nothing was paid',
};

export const REFERRAL_SETTLEMENT_LABEL = {
  PENDING: 'Settlement pending',
  TOKEN_ISSUED: 'Fee carried to a new token',
  CREDITED: 'Fee held as credit',
  REFUNDED: 'Fee refunded',
  NONE: 'Nothing to settle',
};

export const CREDIT_REASON_LABEL = {
  REFERRAL_CREDIT: 'Referral fee held',
  APPLIED_TO_TOKEN: 'Spent on a token',
  CASHED_OUT: 'Paid back in cash',
  ADJUSTMENT: 'Adjustment',
};

export const LOOKALIKE_KIND_LABEL = {
  REGISTERED_ANYWAY: 'Registered despite a look-alike',
  DIFFERENT_PEOPLE: 'Confirmed as different people',
};

export const INTAKE_SECTION_LABEL = {
  FAMILY_HISTORY: 'Family history',
  PERSONAL_HISTORY: 'Personal history',
  BIRTH_HISTORY: 'Birth history',
};

export const INTAKE_ANSWER_LABEL = { YES: 'Yes', NO: 'No', NOT_KNOWN: 'Not known' };

export const CNIC_OF_LABEL = { OWN: '', FATHER: 'father', MOTHER: 'mother', GUARDIAN: 'guardian' };

export const ITEM_TYPE_LABEL = {
  MEDICINE: 'Medicine',
  MEDICAL_SERVICE: 'Service',
  EXTRA_SERVICE: 'Service',
};

/**
 * How a token was paid. NONE means nothing changed hands, which on a token is either a free
 * consultation or one covered by the patient's credit; the shared label reads "Not recorded",
 * which is wrong here. A blank mode is an old token from before the method was recorded.
 */
export const tokenPaymentLabel = (v) => {
  // A blank mode on a token that took no money is a free or credit-covered one like any other;
  // "Not recorded" is only honest when money changed hands and the method was not written down.
  if (!v.paymentMode || v.paymentMode === 'NONE') {
    if (v.paymentMode !== 'NONE' && Number(v.paid) > 0) return 'Not recorded';
    return Number(v.creditUsed) > 0 ? 'Paid by credit' : 'No charge';
  }
  return PAYMENT_MODE_LABEL[v.paymentMode] || human(v.paymentMode);
};

// Labels as the patient file shows them, where the enum's own words would come out wrong
// ("Mri", "Ct Scan", "Stat").
export const DOC_TYPE_LABEL = {
  LAB_REPORT: 'Lab Report',
  XRAY: 'X-Ray',
  CT_SCAN: 'CT Scan',
  MRI: 'MRI',
  ULTRASOUND: 'Ultrasound',
  ECG: 'ECG',
  PRESCRIPTION: 'Prescription',
  DISCHARGE_SUMMARY: 'Discharge Summary',
  REFERRAL: 'Referral Letter',
  CONSENT_FORM: 'Consent Form',
  INSURANCE: 'Insurance Document',
  ID_PROOF: 'ID Proof',
  PHOTO: 'Photo',
  OTHER: 'Other',
};

export const LAB_URGENCY_LABEL = { STAT: 'STAT', URGENT: 'Urgent', ROUTINE: 'Routine', FOLLOW_UP: 'Follow-up' };

/** A completed bill with nothing to pay has no method; one that took money but kept none is old. */
export const billPaymentLabel = (i) => {
  if (!i.paymentMode) {
    if (i.status !== 'PAID') return DASH;
    return Number(i.amountPaid) > 0 ? 'Not recorded' : 'No charge';
  }
  return PAYMENT_MODE_LABEL[i.paymentMode] || human(i.paymentMode);
};

/**
 * The date of birth to hand the vitals engine so it judges a reading by the age the patient WAS on
 * the day it was taken, not the age they are today: a child weighed as an infant is measured
 * against infant ranges. It moves the birth date forward by the time since the visit, since the
 * engine only ever works out age from a birth date and today.
 */
export const dobAsOf = (dateOfBirth, visitDate) => {
  const dob = toClinicDate(dateOfBirth);
  const visit = toClinicDate(visitDate);
  const today = toClinicDate(clinicToday());
  if (!dob || !visit || !today) return dateOfBirth;
  const shifted = new Date(dob.getTime() + (today.getTime() - visit.getTime()));
  const iso = `${shifted.getFullYear()}-${String(shifted.getMonth() + 1).padStart(2, '0')}-${String(shifted.getDate()).padStart(2, '0')}`;
  return iso;
};
